import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Country, CountryCode, NotificationPrefs, Variant } from '@/types/domain';
import { FlagStripe, VariantHeading } from '@/components/primitives';
import { haptics } from '@/lib/native';
import {
  checkPushPermission,
  requestPushPermission,
  type PushPermissionState,
} from '@/lib/push';
import { platform } from '@/lib/platform';
import { useWebPush } from '@/lib/webPush';

// Decorative warning glyph for the push-permission notice. Presentational, not
// language — rendered from a const so it stays out of the translation catalog.
const WARNING_GLYPH = '⚠️';

interface FollowScreenProps {
  variant: Variant;
  countries: Country[];
  /** Country codes that appear in at least one WC match. Used to hide
   * curated-but-non-qualified countries (e.g. Peru, who's only in
   * friendlies). */
  wcParticipantCodes: Set<CountryCode>;
  follows: Partial<Record<CountryCode, boolean>>;
  setFollows: (next: Partial<Record<CountryCode, boolean>>) => void;
  notifPrefs: NotificationPrefs;
  setNotifPrefs: (next: NotificationPrefs) => void;
}

const NOTIF_ROWS: Array<{ k: keyof NotificationPrefs; labelKey: string; subKey: string }> = [
  { k: 'matchStart', labelKey: 'follow:notif.matchStart', subKey: 'follow:notif.matchStartSub' },
  { k: 'newSpots', labelKey: 'follow:notif.newSpots', subKey: 'follow:notif.newSpotsSub' },
];

export function FollowScreen({
  variant,
  countries,
  wcParticipantCodes,
  follows,
  setFollows,
  notifPrefs,
  setNotifPrefs,
}: FollowScreenProps) {
  const { t } = useTranslation();
  const isWeb = platform.isWeb;
  // Web-first: browser permission + a live push subscription is the master
  // switch. Enabling turns every category on (the backend does this); the
  // per-category toggles below let the user turn individual ones back off.
  const webPush = useWebPush();

  // Native (legacy) path keeps its OS-permission tracking so the toggles
  // can't claim notifications are on when the OS prompt was denied.
  const [pushPermission, setPushPermission] = useState<PushPermissionState>(
    platform.isNative ? 'prompt' : 'granted',
  );

  useEffect(() => {
    if (!platform.isNative) return;
    let cancelled = false;
    void checkPushPermission().then((state) => {
      if (!cancelled) setPushPermission(state);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Are the three category toggles live? Only once notifications are enabled.
  const togglesActive = isWeb ? webPush.enabled : pushPermission === 'granted';

  async function handleMasterEnable() {
    haptics.light();
    const result = await webPush.enable();
    // The subscribe call already flipped every category ON server-side; adopt
    // the returned prefs so the store + toggles reflect it without a refetch.
    if (result.ok && result.preferences) setNotifPrefs(result.preferences);
  }

  async function handleToggle(key: keyof NotificationPrefs) {
    const turningOn = !notifPrefs[key];
    if (isWeb) {
      // Toggles are inert until the master switch is on, so just flip the pref.
      if (!webPush.enabled) return;
      setNotifPrefs({ ...notifPrefs, [key]: turningOn });
      return;
    }
    if (turningOn && platform.isNative && pushPermission !== 'granted') {
      const next = await requestPushPermission();
      setPushPermission(next);
      // OS denied (or silently suppressed the prompt on permanent deny).
      // Leave the toggle in its current OFF state and let the status row
      // tell the user why.
      if (next !== 'granted') return;
    }
    setNotifPrefs({ ...notifPrefs, [key]: turningOn });
  }

  function Header() {
    if (variant === 'editorial') {
      return (
        <div style={{ padding: 'max(28px, env(safe-area-inset-top)) 24px 16px', background: '#fbf8f3' }}>
          <div
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 11,
              letterSpacing: 1.4,
              color: '#8a7f72',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            {t('follow:notifications')}
          </div>
          <div
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: 38,
              color: '#1a1612',
              letterSpacing: -1,
              lineHeight: 1,
              fontWeight: 400,
            }}
          >
            <span style={{ fontStyle: 'italic' }}>{t('follow:titleEditorialLead')}</span>{t('follow:titleEditorialTail')}
          </div>
          <div
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 14,
              fontStyle: 'italic',
              color: '#5a544c',
              marginTop: 12,
              lineHeight: 1.4,
            }}
          >
            {t('follow:subtitleEditorial')}
          </div>
        </div>
      );
    }
    if (variant === 'minimal') {
      return (
        <div style={{ padding: 'max(28px, env(safe-area-inset-top)) 20px 12px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#9a9590',
              textTransform: 'uppercase',
              letterSpacing: 1.4,
              marginBottom: 4,
            }}
          >
            {t('follow:notifications')}
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#0a0a0a', letterSpacing: -0.5 }}>
            {t('follow:titleMinimal')}
          </div>
          <div style={{ fontSize: 12, color: '#5a544c', marginTop: 4 }}>
            {t('follow:subtitleMinimal')}
          </div>
        </div>
      );
    }
    return (
      <div style={{ padding: 'max(24px, env(safe-area-inset-top)) 20px 12px' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#9a9590',
            textTransform: 'uppercase',
            letterSpacing: 1.4,
          }}
        >
          {t('follow:notifications')}
        </div>
        <div style={{ fontSize: 28, fontWeight: 900, color: '#0a0a0a', letterSpacing: -0.6, marginTop: 2 }}>
          {t('follow:titleSporty')}
        </div>
        <div style={{ fontSize: 12, color: '#5a544c', marginTop: 4, fontWeight: 500 }}>
          {t('follow:subtitleSporty')}
        </div>
      </div>
    );
  }

  // Follow surface lists every WC 2026 participant so any fan can find
  // their team — independent of whether we've curated venues/tagline
  // for them yet. Non-WC countries (friendlies-only, e.g. Peru) are
  // still excluded.
  const curatedCountries = countries.filter((c) => wcParticipantCodes.has(c.code));
  const followedCount = Object.values(follows).filter(Boolean).length;
  const followedCodes = curatedCountries.map((c) => c.code).filter((c) => follows[c]);

  return (
    <>
      <Header />
      <div style={{ padding: '12px 20px 8px' }}>
        <VariantHeading variant={variant}>{t('follow:notifyMeAbout')}</VariantHeading>
      </div>
      <div
        style={{
          background: '#fff',
          margin: '0 20px',
          borderRadius: 14,
          border: '1px solid #ece8e2',
          overflow: 'hidden',
        }}
      >
        {platform.isNative && pushPermission !== 'granted' ? (
          <div
            role="status"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              background: '#fff8e6',
              borderBottom: '0.5px solid #f0ede8',
            }}
          >
            <div style={{ fontSize: 16 }} aria-hidden="true">
              {WARNING_GLYPH}
            </div>
            <div style={{ flex: 1, fontSize: 12, color: '#5a4a1f', lineHeight: 1.35 }}>
              {pushPermission === 'denied' ? (
                <>
                  {t('follow:systemOffLead')}<strong>{t('follow:systemOffBold')}</strong>{t('follow:systemOffTail')}
                </>
              ) : (
                <>{t('follow:systemNotYet')}</>
              )}
            </div>
          </div>
        ) : null}
        {isWeb ? (
          <div style={{ borderBottom: '0.5px solid #f0ede8' }}>
            {!webPush.supported ? (
              <div
                role="status"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  background: '#fff8e6',
                }}
              >
                <div style={{ fontSize: 16 }} aria-hidden="true">
                  {WARNING_GLYPH}
                </div>
                <div style={{ flex: 1, fontSize: 12, color: '#5a4a1f', lineHeight: 1.35 }}>
                  {t('follow:webUnsupported')}
                </div>
              </div>
            ) : webPush.permission === 'denied' ? (
              <div
                role="status"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  background: '#fff8e6',
                }}
              >
                <div style={{ fontSize: 16 }} aria-hidden="true">
                  {WARNING_GLYPH}
                </div>
                <div style={{ flex: 1, fontSize: 12, color: '#5a4a1f', lineHeight: 1.35 }}>
                  {t('follow:webBlocked')}
                </div>
              </div>
            ) : webPush.enabled ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0a0a0a' }}>
                    {t('follow:webOn')}
                  </div>
                  <div style={{ fontSize: 11, color: '#9a9590', marginTop: 2 }}>
                    {t('follow:webOnSub')}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={webPush.busy}
                  onClick={() => void webPush.disable()}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#c0392b',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: webPush.busy ? 'default' : 'pointer',
                    padding: 4,
                    fontFamily: 'inherit',
                  }}
                >
                  {t('follow:webTurnOff')}
                </button>
              </div>
            ) : (
              <div style={{ padding: '14px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0a0a0a' }}>
                  {t('follow:webEnableTitle')}
                </div>
                <div style={{ fontSize: 12, color: '#7a746c', marginTop: 4, lineHeight: 1.4 }}>
                  {t('follow:webEnableSub')}
                </div>
                <button
                  type="button"
                  disabled={webPush.busy}
                  onClick={() => void handleMasterEnable()}
                  style={{
                    marginTop: 12,
                    width: '100%',
                    background: '#0a0a0a',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    padding: '12px 14px',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: webPush.busy ? 'default' : 'pointer',
                    opacity: webPush.busy ? 0.6 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  {webPush.busy ? t('follow:webBusy') : t('follow:webEnableCta')}
                </button>
              </div>
            )}
          </div>
        ) : null}
        {NOTIF_ROWS.map((row, i) => (
          <div
            key={row.k}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 14px',
              borderBottom: i === NOTIF_ROWS.length - 1 ? 'none' : '0.5px solid #f0ede8',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0a0a0a' }}>{t(row.labelKey)}</div>
              <div style={{ fontSize: 11, color: '#9a9590', marginTop: 2 }}>{t(row.subKey)}</div>
            </div>
            <button
              type="button"
              disabled={!togglesActive}
              onClick={() => {
                void handleToggle(row.k);
              }}
              style={{
                width: 44,
                height: 26,
                borderRadius: 13,
                background: togglesActive && notifPrefs[row.k] ? '#34c759' : '#e3e1da',
                border: 'none',
                position: 'relative',
                cursor: togglesActive ? 'pointer' : 'default',
                flexShrink: 0,
                opacity: togglesActive ? 1 : 0.5,
                transition: 'background .2s, opacity .2s',
              }}
              aria-pressed={togglesActive && notifPrefs[row.k]}
              aria-disabled={!togglesActive}
              aria-label={t(row.labelKey)}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 2,
                  left: togglesActive && notifPrefs[row.k] ? 20 : 2,
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  transition: 'left .2s',
                }}
              />
            </button>
          </div>
        ))}
      </div>
      <div style={{ padding: '24px 20px 0' }}>
        <VariantHeading variant={variant}>{t('follow:countries')}</VariantHeading>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
          padding: '0 20px',
        }}
      >
        {curatedCountries.map((co) => {
          const c = co.code;
          const followed = !!follows[c];
          return (
            <button
              key={c}
              type="button"
              onClick={() => {
                // Sound removed — haptic alone is the feedback now.
                haptics.light();
                setFollows({ ...follows, [c]: !followed });
              }}
              style={{
                position: 'relative',
                textAlign: 'left',
                background: followed
                  ? `linear-gradient(135deg, ${co.colors.primary}1a, ${co.colors.secondary}10)`
                  : '#fff',
                border: `1.5px solid ${followed ? co.colors.primary : '#ece8e2'}`,
                borderRadius: 14,
                padding: '12px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FlagStripe code={co.code} colors={co.colors} w={26} h={18} />
                <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#0a0a0a' }}>{co.name}</div>
                {followed && (
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 9,
                      background: co.colors.primary,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    {t('common:check')}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 10, color: '#7a746c', marginTop: 8, lineHeight: 1.3 }}>
                {co.neighborhoods[0]}
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ padding: '20px 20px 0' }}>
        <div
          style={{
            background: '#0a0a0a',
            color: '#fff',
            padding: '12px 14px',
            borderRadius: 12,
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {t('follow:following', { count: followedCount })}
          </div>
          <div style={{ opacity: 0.7 }}>
            {followedCodes.length > 0
              ? t('follow:pingsWhenSome', {
                  count: followedCodes.length,
                  teams: followedCodes.slice(0, 3).join(', '),
                })
              : t('follow:pingsWhenNone')}
          </div>
        </div>
      </div>
      <div style={{ height: 100 }} />
    </>
  );
}
